import { css } from '@emotion/react';
import { Box, Text } from '@inflearn/ds-react-next';
import { useTranslation } from 'next-i18next';

import { generateImageSrc } from '../../../../utils/generateImageSrc';

import { SectionHead } from './SectionHead';
import { LANDING_COLOR, LANDING_FONT, LANDING_MOBILE_MQ, LANDING_SPACE } from './constants';
import { FEATURE_SCREEN_SRC } from './featureScreens';

const STEPS = [
  { key: 'pick', src: FEATURE_SCREEN_SRC.interviewerPick },
  { key: 'questions', src: FEATURE_SCREEN_SRC.questions },
  { key: 'interview', src: FEATURE_SCREEN_SRC.interview },
  { key: 'report', src: FEATURE_SCREEN_SRC.report },
] as const;

/** 캡처 원본 비율 — 칸을 같은 비율로 맞춰 잘리지 않고 가운데 기준으로 축소되게 한다 */
const SCREEN_RATIO = '1600 / 985';
/** 원본 1600px 을 카드 크기(최대 약 260px)로 브라우저가 축소하면 글자 획이 깨져 CDN 리사이즈를 쓴다 */
const SHOT_WIDTH = 260;

const DOT_SIZE = 30;
const TIMELINE_LINE = '2px dashed rgba(255,255,255,.25)';

export const HowItWorks = () => {
  const { t } = useTranslation();

  return (
    <Box component="section" css={styles.section} data-reveal aria-labelledby="ai-how-title">
      <Box css={styles.inner}>
        <SectionHead id="ai-how-title" titleKey="ai_interviews.landing.how.title" tone="dark" />

        <Box component="ol" css={styles.steps}>
          {STEPS.map((step, index) => (
            <Box key={step.key} component="li" css={styles.step}>
              <Text component="span" css={styles.dot} aria-hidden>
                {index + 1}
              </Text>
              <Box css={styles.copy}>
                <Text component="h3" css={styles.title}>
                  {t(`ai_interviews.landing.how.${step.key}.title`)}
                </Text>
                <Text css={styles.desc}>{t(`ai_interviews.landing.how.${step.key}.desc`)}</Text>
              </Box>
              <Box css={styles.shot}>
                {/* eslint-disable-next-line @next/next/no-img-element -- 진행 단계 캡처, 어드민 CDN */}
                <img
                  src={generateImageSrc(step.src, SHOT_WIDTH)}
                  alt={t(`ai_interviews.landing.how.${step.key}.image_alt`)}
                  width={1600}
                  height={985}
                  css={styles.shotImage}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  section: css({
    background: LANDING_COLOR.dark,
    padding: `${LANDING_SPACE.sectionY} ${LANDING_SPACE.pageX}`,
  }),
  inner: css({
    maxWidth: 1200,
    margin: '0 auto',
  }),
  steps: css({
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 20,
    margin: `${LANDING_SPACE.blockTop} 0 0`,
    padding: '52px 0 0',
    listStyle: 'none',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '12%',
      right: '12%',
      top: 26,
      borderTop: TIMELINE_LINE,
    },
    [LANDING_MOBILE_MQ]: {
      gridTemplateColumns: '1fr',
      gap: 14,
      padding: '0 0 0 44px',
      '&::before': {
        left: 14,
        right: 'auto',
        top: 10,
        bottom: 10,
        borderTop: 0,
        borderLeft: TIMELINE_LINE,
      },
    },
  }),
  step: css({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 24,
    background: '#1B1F24',
    border: '1px solid rgba(255,255,255,.08)',
    transition: 'border-color 180ms ease',
    '&:hover': {
      borderColor: 'rgba(255,255,255,.32)',
    },
    [LANDING_MOBILE_MQ]: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) 112px',
      alignItems: 'center',
    },
  }),
  dot: css({
    position: 'absolute',
    top: -(DOT_SIZE + 6),
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'grid',
    placeItems: 'center',
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: '50%',
    background: LANDING_COLOR.mint,
    boxShadow: `0 0 0 5px ${LANDING_COLOR.dark}`,
    color: '#062B24',
    fontSize: 14,
    fontWeight: 900,
    [LANDING_MOBILE_MQ]: {
      top: '50%',
      left: -44,
      transform: 'translateY(-50%)',
    },
  }),
  copy: css({
    minHeight: 150,
    padding: '32px 24px 24px',
    textAlign: 'center',
    [LANDING_MOBILE_MQ]: {
      minHeight: 0,
      padding: 16,
      textAlign: 'left',
    },
  }),
  title: css({
    margin: 0,
    color: '#fff',
    fontSize: LANDING_FONT.sectionTitleSm,
    fontWeight: 800,
    letterSpacing: '-0.02em',
    wordBreak: 'keep-all',
  }),
  desc: css({
    marginTop: 6,
    color: 'rgba(255,255,255,.65)',
    fontSize: LANDING_FONT.bodyMd,
    lineHeight: 1.6,
    wordBreak: 'keep-all',
  }),
  shot: css({
    aspectRatio: SCREEN_RATIO,
    margin: 'auto 20px 20px',
    overflow: 'hidden',
    borderRadius: 14,
    background: '#fff',
    [LANDING_MOBILE_MQ]: {
      margin: '12px 12px 12px 0',
    },
  }),
  shotImage: css({
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    objectPosition: 'center',
  }),
};
